
import {Component, Inject, Prop, Vue, Watch} from "vue-property-decorator";
	import AccomodationService from "@/services/AccomodationService";
	import ApiHelper from "@/helpers/api-helper";
	import {contarPedido, ESPERA_DE_PRECIOS, EsperaDePrecios} from "@/helpers/espera-de-precios";
	import {opcionalesDelTramo} from "@/helpers/tramo-helper";
	import {Cambio, CambiosAlGuardar, opcionalesQueCambian, Quitado, sinCambios} from "@/helpers/ventana-al-guardar";

	/**
	 * N-287 · Los extras elegidos ({id, name, price}). Los del alojamiento llevan siempre el id de
	 * CATÁLOGO, también los que trae el GET del presupuesto (`BudgetResource`).
	 */
	function extrasElegidos(value: any): any[] {
		return (value.extra_ids || []).map((id) => {
			const elegido = (value.extras || []).find((extra) => extra.id == id);
			return { id, name: elegido ? elegido.name : null, price: elegido ? elegido.price : null };
		});
	}

	@Component({})
	export default class AccommodationSelect extends Vue {
		// N-288 · 4A: GUARDAR espera mientras el catálogo de precios está en camino.
		@Inject({ from: ESPERA_DE_PRECIOS, default: null }) private readonly esperaDePrecios!: EsperaDePrecios | null;
		@Prop({ default: null, required: false }) private model;
		@Prop({ default: null, required: true }) private errors;
		@Prop({ default: null, required: false }) private valid;
		@Prop({ default: null, required: true }) private travel_id;
		@Prop({ default: null, required: true }) private people;

		private items: any[] = [];
		private total = 0;
		private selected = {};
		private value: {
			id: any;
			name: any;
			prices: any[];
			extras: any[];
			extra_ids: any[];
			increase_half_board: number;
			increase_full_board: number;
		} = {
			id: null,
			name: null,
			prices: [],
			extras: [],
			extra_ids: [],
			increase_half_board: 0,
			increase_full_board: 0
		};
		private filters: any;
		// N-287 B4: los extras que pidió el cliente (o marcó el operador), para remapearlos en
		// cada tramo. `null` = aún no se han leído: se leen al llegar el primer catálogo.
		private extrasPedidos: any[] | null = null;
		// N-288 · T3: los extras vistos en cualquier catálogo (`{id: extra}`): dicen hasta dónde llegaba
		// el que se quita («llega a 55»).
		private extrasVistos: any = {};

		@Watch("model")
		model_change(value) {
			this.value = value;
			this.extrasPedidos = null;
		}

		@Watch("travel_id")
		travel_id_change(value) {
			this.filters.travel_id = value;
			if (this.filters.travel_id && this.filters.people != null) {
				this.getSelectorList();
			}
		}

		@Watch("people")
		people_change(value) {
			this.filters.people = value;
			if (this.filters.travel_id && this.filters.people != null) {
				this.getSelectorList();
			}
		}

		constructor() {
			super();
			this.filters = { travel_id: null, people: null };
		}

		mounted() {
			if (this.model) {
				this.value = this.model;
			}
			if (this.travel_id) {
				this.filters.travel_id = this.travel_id;
			}
			if (this.people != null) {
				this.filters.people = this.people;
			}
			if (this.filters.travel_id && this.filters.people != null) {
				this.getSelectorList();
			}
		}

		getSelectorList() {
			const pedidoCon = JSON.stringify(this.filters);
			contarPedido(this.esperaDePrecios, AccomodationService.getSelectorList(null, this.filters)
				.then((res) => {
					// N-287: al teclear «30» también se pide el catálogo de «3» + profesores; si esa
					// respuesta llega la última, no debe pisar a la buena.
					if (pedidoCon !== JSON.stringify(this.filters)) {
						return;
					}
					if (res.status === 200) {
						this.items = res.data.elements;
						for (const accommodation of this.items) {
							for (const extra of (accommodation.extras && accommodation.extras.extras) || []) {
								this.extrasVistos[extra.id] = extra;
							}
						}
						// N-287 B2A: los alojamientos que tienen tarifa para este número, para decir
						// cuál sirve si el elegido se queda sin ninguna.
						this.$emit('catalogo', this.items.map((accommodation) => ({ id: accommodation.id, name: accommodation.name })));
						const elegido = this.items.find((accommodation) => this.value.id == accommodation.id);
						if (elegido) {
							this.renovarTramo(elegido);
						} else if (this.value.id) {
							// N-287 B2A: sin tarifa para este número, el hotel no sale en el catálogo. Su
							// nombre se queda a la vista (la pestaña de precios dice por qué), y lo demás,
							// como estaba: no se puede guardar hasta que haya tarifa.
							this.items.push({ id: this.value.id, name: this.value.name, extras: { prices: [], extras: [] } });
						}
					}
				})
				.catch((error) => {
					ApiHelper.handleErrors(error, {}, this.$bus);
				}));
		}

		/**
		 * N-287 B4: el catálogo se ha renovado y el alojamiento sigue en él. Los extras pasan al del
		 * mismo nombre del tramo nuevo, o se quitan (como el transporte y las actividades), siempre
		 * desde lo PEDIDO, para que un número intermedio no los haga olvidar.
		 */
		renovarTramo(accommodation) {
			if (this.extrasPedidos === null) {
				this.extrasPedidos = extrasElegidos(this.value);
			}
			const extras = opcionalesDelTramo(this.extrasPedidos, accommodation.extras.extras || []);
			this.value.extras = extras;
			this.value.extra_ids = extras.map((extra) => extra.id);
			this.setSelected(false);
			this.$emit('increases', {half_board: accommodation['increase_half_board'] ?? 0, full_board: accommodation['increase_full_board'] ?? 0 })
		}

		/**
		 * N-288 · T3 · Para la ventana de GUARDAR: los extras pedidos que el tramo nuevo quitó o pasó a
		 * otro de otro precio (con la regla de `renovarTramo`). Si el hotel se cambió a mano, lo pedido
		 * empieza de cero y no hay nada que contar.
		 */
		cambiosAlGuardar(): CambiosAlGuardar {
			if (this.extrasPedidos === null || !this.items.length) {
				return sinCambios();
			}
			const personas = Number(this.people);
			const hotel = this.value.name || 'Alojamiento';
			const { quitados, cambiados } = opcionalesQueCambian(this.extrasPedidos, this.value.extras || []);
			return {
				quitan: quitados.map((pedido): Quitado => {
					const visto = this.extrasVistos[pedido.id];
					const tope = visto && Number(visto.people_max) < personas ? 'llega a ' + visto.people_max : '';
					return { texto: hotel + ' · ' + pedido.name, motivo: tope, causa: 'personas' };
				}),
				cambian: cambiados.map((cambio): Cambio => ({
					texto: hotel + ' · ' + cambio.ahora.name, antes: cambio.antes.price, ahora: cambio.ahora.price, unidad: '',
				})),
			};
		}

		emitValues(){
			this.$emit('input', this.value);
			this.$emit('increases', {
				half_board: this.value['increase_half_board'],
				full_board: this.value['increase_full_board'],
			});
			this.$emit('setPrice', this.value);
		}

		setSelected(clear_extras = true) {
			this.selected = {};
			if (clear_extras) {
				this.value.extras = [];
				this.value.extra_ids = [];
				this.extrasPedidos = [];
			}
			for (const accommodation of this.items) {
				if (this.value.id == accommodation.id) {
					this.selected = accommodation;
					this.value.name = accommodation.name;
					this.value.prices = accommodation.extras.prices;
					this.value.increase_half_board = accommodation.increase_half_board;
					this.value.increase_full_board = accommodation.increase_full_board;
					this.totalPrice()
					break;
				}
			}
			this.emitValues();
		}

		totalPrice(){
			let total = 0;
			if(this.value.id && this.value.prices){
				for (const price of this.value.prices) {
					total += price['price'];
				}
			}
			this.total = total;
		}

		setExtras() {
			this.value.extras = [];
			for (const extra of this.selected['extras']['extras']) {
				if (this.value.extra_ids.indexOf(extra['id'])  > -1) {
					this.value.extras.push(extra);
				}
			}
			// N-287 B4: lo que el operador marca o desmarca a mano pasa a ser lo pedido.
			this.extrasPedidos = extrasElegidos(this.value);
			this.emitValues();
		}
	}
